// ===== 請われぬ介錯 — 刃⇔腕砲の二形態変形 =====
// （キーは `blade`。呼び名は「刃」= 幅広の刃。2026-08-18 に `maul` から改名した。下の 🔴🔴 を読むこと）
//
// 幕1は刃だけで5技の読みを教え、幕2から腕砲と画面上の変形アクションを解禁する。
// 武器は弾かれても壊れず、柄・ゴミ山・拾い直し・無防備特例は現行機構に持ち込まない。
// 正本 = docs/decision-ledger.md §請われぬ介錯 2026-08-12
//
// ⚠️ このファイルは types.ts を import しない (types.ts 側が ExecutionerRuntime を参照するため)。
import { backStepProgress, stepWeightSquashY } from './exec_step_weight'

import { getAttack } from './attacks'
import { cannonFajinEnabled } from './executioner_cannon_fajin'

// 🔵 2026-09-03 第4便 ユーザー裁定「320はまだ近い」: 砲の間合いを実行時に切り替え可能にし、既定を 400px へ。
//   実行経路 (rangeTuningFor の cannon 分岐・boss.ts の距離維持) は必ず cannonRangeTuning() を読む。
//   下の EXECUTIONER.ranges.cannon / EXECUTIONER.keepDistance は同じ 400 基準の値を持つが、
//   ?cannonRange= で実行時に変えても追従しない「参照禁止」の値 (他所からの参照・比較用に残すだけ)。
let cannonRangePx = 400

export type ExecutionerCannonRangeTuning = {
  preferredRange: number
  attackCommitRange: number
  tooClosePx: number
  retreatToPx: number
  maxRetreatPx: number
  maxConsecutive: number
  minWallRoomPx: number
  sweepWithinPx: number
}
export function cannonRangeTuning(): ExecutionerCannonRangeTuning {
  const n = cannonRangePx
  return {
    preferredRange: n, attackCommitRange: n + 16, tooClosePx: Math.round(n * 0.75),
    retreatToPx: n, maxRetreatPx: n - 90, maxConsecutive: 2, minWallRoomPx: 60, sweepWithinPx: 150,
  }
}
export function getExecutionerCannonRange(): number { return cannonRangePx }
export function setExecutionerCannonRange(px: number): void {
  if (!Number.isInteger(px) || px < 200 || px > 600) return
  cannonRangePx = px
  // 直射・速射の弾の下向き角度＝砲口の高さ 98 から膝 (床上 30px) へ n px 先で届く角度
  const deg = Math.round(Math.atan2(98 - 30, px) * 180 / Math.PI)
  getAttack('exec_cannonShot').castProjectile!.descentDeg = deg
  getAttack('exec_cannonVolley').castProjectile!.descentDeg = deg
}

// ===== 2026-09-04 砲形態の噛み合わせ（実プレイ 2026-09-04「砲形態が薙ぎループで死ぬ」への対処） =====
//   正本 = docs/candidates/2026-09-04-executioner-cannon-tempo-design.md
//   案は実プレイで比べる前提で、実行時に切り替えられる (?cannonMode= / ?cannonScore= ・ __setCannonMode / __setCannonScore)。
//   🔴 どの案でも共通で直したもの (バグ扱い・切替なし):
//     ① 砲身薙ぎは変形カウンタ (attacksSinceTransform) に数えない＝砲形態の長さは「弾を撃った譜面の数」で決まる。
//     ② 砲身薙ぎの後は冷却 (sweepCooldownMs) が入り、その間は密着でも薙がずに距離を取り直す＝薙ぎが連発しない。
//     ③ 当てた時の吹き飛ばしを 120→240 (薙ぎの届く 150 の外まで押す)。
//
//   cannonMode (薙ぎの性格):
//   🔵 2026-09-06 ユーザー裁定 (実プレイ検収): 案2「弾いたら殴れる」＝'duel' が採用 → 既定を 'shove' から変更。
//     'shove'        = 「どけ」。弾かれても 240px 押し返す (parryKnockbackPx)。砲形態は近接の決闘に付き合わない。
//       (比較用に残す。?cannonMode=shove / __setCannonMode('shove') で今も選べる)
//     'duel' (既定)  = 「一度は付き合う」。弾かれたら押さない＝薙ぎの硬直 900ms を殴れる。ただし冷却で2発目は無い。
//   cannonScore (砲の譜面):
//     'composed' (既定) = 榴弾→直射 / 榴弾→速射 の2拍譜面を主にする＝「落ちてくる榴弾を見ながら弾く」を意図して組む。
//     'single'          = 09-03 までの単発3本だけ (比較用)。
export type ExecutionerCannonMode = 'shove' | 'duel'
export type ExecutionerCannonScore = 'composed' | 'single'
export const EXECUTIONER_CANNON_SWEEP = {
  cooldownMs: 4000,        // 薙ぎの後、次の薙ぎまでの最短。距離を取り直して最低1譜面は撃てる長さ (後退 ~0.5s + 譜面 ~3s)
  hitKnockbackPx: 240,     // 当てた時の吹き飛ばし (旧 120 = 薙ぎの届く 150 の中に残っていた)
  shoveParryKnockbackPx: 240, // 'shove' で弾かれた時にも押す量
} as const
let cannonMode: ExecutionerCannonMode = 'duel'
let cannonScore: ExecutionerCannonScore = 'composed'
export function getExecutionerCannonMode(): ExecutionerCannonMode { return cannonMode }
export function setExecutionerCannonMode(mode: ExecutionerCannonMode): void {
  cannonMode = mode
  const sweep = getAttack('exec_cannonSweep')
  sweep.hitKnockbackPx = EXECUTIONER_CANNON_SWEEP.hitKnockbackPx
  sweep.parryKnockbackPx = mode === 'shove' ? EXECUTIONER_CANNON_SWEEP.shoveParryKnockbackPx : undefined
}
export function getExecutionerCannonScore(): ExecutionerCannonScore { return cannonScore }
// 譜面の切替は bosses.ts の strings を実行時に読む側 (boss.ts pickNextString) が
//   executionerStringEnabled() で絞る＝定義は両方持ったまま、片方を候補から外す。
export function setExecutionerCannonScore(score: ExecutionerCannonScore): void { cannonScore = score }
export const EXECUTIONER_COMPOSED_CANNON_STRING_IDS = ['exec-cannon-mortar-shot', 'exec-cannon-mortar-volley', 'exec-cannon-mortar-fajin'] as const
export const EXECUTIONER_SINGLE_ONLY_CANNON_STRING_IDS = ['exec-cannon-mortar'] as const
export function executionerStringEnabled(stringId: string): boolean {
  if (stringId === 'exec-cannon-mortar-fajin' && !cannonFajinEnabled()) return false
  if (cannonScore === 'single') {
    return !(EXECUTIONER_COMPOSED_CANNON_STRING_IDS as readonly string[]).includes(stringId)
  }
  // composed: 単発の榴弾は外す (榴弾は必ず「次の弾」とセットで来る＝撃ち逃げの意図を譜面で作る)
  return !(EXECUTIONER_SINGLE_ONLY_CANNON_STRING_IDS as readonly string[]).includes(stringId)
}

export const EXECUTIONER = {
  weapons: {
    // 刃の damage/posture 倍率は 2026-09-03 に撤去（表の値がそのまま届く）。gapMul 1.15 だけ
    // 残す＝刃は一撃ごとに構え直す。砲は中立。
    // 正本 = docs/candidates/2026-09-03-executioner-combat-numbers-fable5-design.md §3
    blade: { gapMul: 1.15, damageMul: 1, postureMul: 1 },
    cannon: { gapMul: 1.0, damageMul: 1.0, postureMul: 1.0 }, // ⬜ドラフト
  },
  ranges: {
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 preferredRange 170 / attackCommitRange 190）
    blade: { preferredRange: 136, attackCommitRange: 152 },
    // 🔴 参照禁止 (2026-09-03 第4便)。実行経路は cannonRangeTuning() を読む。
    //   ここは 400 基準の値を保つだけの参照用 (executioner_step.test.ts 等の固定値テストが読む)。
    cannon: { preferredRange: 400, attackCommitRange: 416 },
  },
  keepDistance: {
    // 🔴 参照禁止 (2026-09-03 第4便)。実行経路は cannonRangeTuning() を読む。
    //   ここは 400 基準の値を保つだけの参照用 (executioner_step.test.ts 等の固定値テストが読む)。
    tooClosePx: 300,
    retreatToPx: 400,
    maxRetreatPx: 310,
    maxConsecutive: 2,
    minWallRoomPx: 60,
  },
  step: {
    common: {
      // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 40）
      minStepPx: 32,
      // 刃 152-136 / 砲 336-320。ranges の攻撃開始帯と同じ16pxだけは踏み込まない。
      // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 20）
      deadbandPx: 16,
      crouchMs: 150,
      landMs: 200,
      minSettleMs: 180,
      maxTravelMsHardLimit: 200,
      afterimageLifeMs: 140,
    },
    styles: {
      shunpo: {
        // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 220）
        maxStepPx: 176,
        travelMode: 'fixed',
        travelMs: 67,
        ease: 'linear',
        afterimageCount: 3,
        afterimageAlpha: 0.42,
        overshootPx: 6,
        overshootMs: 120,
      },
      lunge: {
        // 🔵 2026-09-03 第3便 ユーザー裁定: 230px へ（旧 184＝2026-09-02夜 等身1.6化で0.8倍・旧230）
        maxStepPx: 230,
        travelMode: 'bySpeed',
        travelSpeedPxPerS: 1000,
        minTravelMs: 100,
        ease: 'easeOutQuint',
        afterimageCount: 2,
        afterimageAlpha: 0.30,
        // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 10）
        overshootPx: 8,
        overshootMs: 120,
      },
    },
    // 🔵 2026-09-02 夜 等身 1.6 化で px/minMovePx を 0.8 倍（旧 px 70 / minMovePx 20）
    back: { px: 56, travelMs: 33, freezeMs: 230, cooldownMs: 800, minMovePx: 16 },
  },
  // 2026-08-13 夜 ユーザー裁定: 刃の腕で主人公を押しのけ、その腕を砲へ組み変える。
  // 正本 = docs/candidates/2026-08-13-executioner-phase2a-morph-backstep-and-range-ticket.md
  transform: {
    attacksBeforeCheck: 3, // ⬜ドラフト: 変形判定を始めるまでの攻撃数
    chance: 0.5,           // ⬜ドラフト: 条件成立後、1手ごとの変形確率
    durationMs: 1200,      // ⬜ドラフト: 見える変形アクションの尺
    shoveMs: 400,          // ⬜ドラフト: 通常時の突き飛ばし区間
    shoveHitAtMs: 220,     // ⬜ドラフト: 接触して押し出す瞬間（通常時）
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 180）
    shoveReachPx: 144,     // ⬜ドラフト: この距離以内にいる主人公だけ押す
    // 🔵 2026-09-03 第4便: 砲の間合い 400 に合わせて 264（= 400 − 136。旧 184＝第3便の320基準）
    shovePushPx: 264,      // ⬜ドラフト: 砲と刃の好みの間合い差と同じ押し出し量
    shoveTurnMs: 160,      // ⬜ドラフト: 背面時だけ turn に使い、以後の時刻と総尺を遅らせる
    cannonHoldMs: 20000,   // ⬜ドラフト: 砲形態に変わった直後、この時間は刃へ戻る判定そのものを止める「時間の床」
  },
  openingWeapon: 'blade' as const,
} as const

// 🔴🔴 **`'maul'`（大槌）は歴史的な名前だった。武器は 2026-08-15 に「幅広の刃」へ変わり、
//   2026-08-18 にキーも `blade` へ改名した（票 = docs/candidates/2026-08-18-executioner-blade-27-install-ticket.md §8）。**
//   **槌の絵へ戻さないこと。呼び名は「刃（は）」と「砲」。**
//   🔴 廃止 = ~~据え置く（表示に一切出ないから）~~ = **事実誤認だった。** 2026-08-18 に現物確認したところ
//      boss-motion-lab.html / scale-review.html / kensyu.html に「大槌 (maul)」が人の目に出ていた。
//   ⚠️ 技のID（`exec_maulDrop` / `exec_maulSet` / `exec_maulHold` / `exec_maulQuake` / `exec_maulJab`）は
//      **改名の対象外**（2026-08-18 裁定・波及が大きいため据え置き）。ただし遊ぶ人に見える日本語ラベルは
//      「落槌」→「振り下ろし」へ変更（`src/attacks.ts` の `label`）。
//   出所と経緯 = docs/decision-ledger.md の 2026-08-15 / 2026-08-16 / 2026-08-18 の行
//              / docs/reports/2026-08-14-executioner-artstyle-root-cause.md
export type ExecutionerWeapon = 'blade' | 'cannon'
export type ExecutionerHeld = ExecutionerWeapon
export type ExecutionerStepStyle = 'shunpo' | 'lunge'
export type ExecutionerStepPhase = 'crouch' | 'travel' | 'land' | 'settle'
export type ExecutionerStepState = {
  kind: 'forward' | 'back'
  style: ExecutionerStepStyle
  phase: ExecutionerStepPhase
  elapsedMs: number
  fromX: number
  toX: number
  settleMs: number
  direction: -1 | 1
  commitOnLand: boolean
  // 長い一歩 (咆哮の新版の幕2の頭で真ん中へ移る時だけ・src/exec_roar_center.ts)。歩幅の上限 (230px) を外し、
  //   移動の上限時間 (200ms) を長い一歩用に延ばす = 速さはいつもの一歩と同じ 1000px/秒のまま、距離だけ長い。未指定 = 今のまま。
  long?: true
  // 致命「執行」の寄りの一歩 (src/exec_finisher_step.ts) だけが持つ、この一歩の踏み込みと沈みの長さ。残像・沈みの描き方がこれに合わせる。未指定 = 設定どおり。
  travelMs?: number
  crouchMs?: number
}
export type ExecutionerStepConfig = typeof EXECUTIONER.step
export type ExecutionerStepStyleConfig = ExecutionerStepConfig['styles'][ExecutionerStepStyle]
export type ExecutionerStepAdvance = { step: ExecutionerStepState | null; x: number }
export type ExecutionerStepVisual = {
  bodyVisible: boolean
  bodyOffsetX: number
  ghosts: { x: number; alpha: number }[]
  // 足元を軸にした縦の倍率 (?stepWeight の沈み・潰れ・src/exec_step_weight.ts)。今の版は 1。
  bodyScaleY: number
  // 踏み出しからの経過 (沈みの間は -1)・移動にかかる時間。足元の土ぼこり (src/exec_step_dust.ts・10-29) が使う。
  ageMs: number
  travelMs: number
}
export type ExecutionerRuntime = {
  held: ExecutionerHeld
  attacksSinceTransform: number
  transformCount: number
  morph: 'no' | 'morphing'
  morphElapsedMs: number
  // null は変形開始後まだ主人公との前後関係を確定していない状態。
  morphTurnRequired: boolean | null
  step: ExecutionerStepState | null
  backstepCooldownMs: number
  consecutiveKeepDistanceRetreats: number
  // 🔵 2026-09-04 砲形態の噛み合わせ: 砲身薙ぎ (exec_cannonSweep) を撃った後の冷却。
  //   0 より大きい間は密着 (sweepWithinPx) でも薙ぎを強制せず、距離維持の後退へ落とす＝薙ぎが連発しない。
  //   正本 = docs/candidates/2026-09-04-executioner-cannon-tempo-design.md
  sweepCooldownMs: number
  // 🔵 2026-09-05「砲形態が短すぎる」対処: 砲形態でいる間だけ毎tick積み上がる経過時間（刃形態では0に戻す）。
  //   shouldStartTransform の「時間の床」がこれを読む。boss.ts の常時減衰ブロックで進める。
  cannonHeldMs: number
  // 🔵 2026-10-02 ユーザー裁定 (通しパック P-2「理不尽」→ A＋B): 自分の床ハザード (榴弾) が
  //   着弾し終わるまでの残り時間。0 より大きい間は組み変わり (突き飛ばし) を始めずに待つ＝
  //   突き飛ばしで着弾の帯へ押し込まない。boss.ts の updateBoss が telegraph 突入で積み、毎tick減らす。
  hazardPendingMs?: number
}

export function createExecutionerRuntime(): ExecutionerRuntime {
  return {
    held: EXECUTIONER.openingWeapon,
    attacksSinceTransform: 0,
    transformCount: 0,
    morph: 'no',
    morphElapsedMs: 0,
    morphTurnRequired: null,
    step: null,
    backstepCooldownMs: 0,
    consecutiveKeepDistanceRetreats: 0,
    sweepCooldownMs: 0,
    cannonHeldMs: 0,
  }
}

// 既定 = 'lunge' (2026-08-23 夕 ユーザー裁定: ラボで2型を並べて「前ステップを残す・瞬歩を捨てる」)。
//   🔴 'shunpo' のコードは消さない (常時ルール4。ラボの切替と ?stepStyle=shunpo で今も見られる)。
//   台帳 = docs/decision-ledger.md 2026-08-23 行 / 票 = docs/candidates/2026-08-23-executioner-step-v2-shunpo-ticket.md
let stepStyle: ExecutionerStepStyle = 'lunge'
export function setExecutionerStepStyle(style: ExecutionerStepStyle): void { stepStyle = style }
export function getExecutionerStepStyle(): ExecutionerStepStyle { return stepStyle }

export function setExecutionerDownswingChargeMs(ms: number): void {
  if (!Number.isInteger(ms) || ms < 300 || ms > 3000) return
  getAttack('exec_maulDrop').chargeMs = ms
}

export function setExecutionerMaulSetCreepPx(px: number): void {
  if (!Number.isInteger(px) || px < 0 || px > 300) return
  getAttack('exec_maulSet').telegraphCreepDistance = px
}

// 🔵 2026-09-05 「砲形態が短すぎる（すぐ刃に戻る）」への対処: 変形直後は一定時間、刃へ戻る判定
//   そのものを止める「時間の床」。EXECUTIONER.transform.cannonHoldMs と同じ値の実行時つまみ
//   (?cannonHold= / __setCannonHold)。EXECUTIONER は as const の凍結オブジェクトなので直接は
//   書き換えられない＝cannonRangePx と同じ「別の mutable 変数 + getter」方式にする。
//   実行経路 (shouldStartTransform) は必ず getExecutionerCannonHoldMs() を読む。
let cannonHoldOverrideMs: number | null = null
export function getExecutionerCannonHoldMs(): number {
  return cannonHoldOverrideMs ?? EXECUTIONER.transform.cannonHoldMs
}
export function setExecutionerCannonHoldMs(ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return
  cannonHoldOverrideMs = ms
}

export type ExecutionerMortarSpreadId = 'A' | 'B'
// 🔵 2026-09-03 第3便: 榴弾の3発の落とし方。A=主人公中心に横3列(20pxの隙間・走れば抜ける) / B=隙間なしの一帯(走り切れない・回避の無敵で1発をやり過ごす)。
//   ユーザーが両方プレイして決める（決まったら片方を既定にして、この切替は残してよい）。
// 🔵 2026-09-03 第4便 ユーザー裁定: A を既定に確定。B は切替として残す。
export const EXECUTIONER_MORTAR_SPREADS: Record<ExecutionerMortarSpreadId, ReadonlyArray<{ offset: number; width: number }>> = {
  A: [{ offset: -110, width: 90 }, { offset: 0, width: 90 }, { offset: 110, width: 90 }],
  B: [{ offset: -190, width: 190 }, { offset: 0, width: 190 }, { offset: 190, width: 190 }],
}
let currentMortarSpread: ExecutionerMortarSpreadId = 'A'
export function getExecutionerMortarSpread(): ExecutionerMortarSpreadId { return currentMortarSpread }
export function setExecutionerMortarSpread(id: ExecutionerMortarSpreadId): void {
  if (id !== 'A' && id !== 'B') return
  currentMortarSpread = id
  // 🔵 2026-09-03 第4便 ユーザー裁定: 撃つ 450ms → 技は 1250ms で終わり、着弾は telegraphMs 2000ms へ (旧1400)。
  //   「落ちてきている間には次の行動に移っている状態にしたい」＝撃ったらすぐ次へ、弾は独立して落ちる。
  getAttack('exec_cannonMortar').hazardSpawns = EXECUTIONER_MORTAR_SPREADS[id].map((s) => ({
    anchor: 'player', offset: s.offset, width: s.width, telegraphMs: 2000, activeMs: 360,
    damage: 12, postureDamage: 28, hitOnce: true, kind: 'quake',
  }))
}

export type ExecutionerMortarRowCount = 3 | 5
// 🔵 2026-09-05「榴弾から逃げるのがタダ」対処: 主人公中心の横列を3列→5列(既定)へ増やし、走る距離を
//   要るようにした。隙間20px(width90・offset差110)の思想は崩さない＝立ち止まると当たると走れば抜ける。
//   新しい2列も既存3列とまったく同じ値。実行時に3列/5列を比べられる (?mortarRows= / __setMortarRows)。
//   🔴 EXECUTIONER_MORTAR_SPREADS (A/B) とは別の切替＝どちらを後に呼んでも上書きし合う (両立しない)。
export const EXECUTIONER_MORTAR_ROW_OFFSETS: Record<ExecutionerMortarRowCount, readonly number[]> = {
  3: [-110, 0, 110],
  5: [-220, -110, 0, 110, 220],
}
// 🔴 既定は3列。5列は 2026-09-05 に試して不採用（壁際で逃げ切れない＝hazard_escape / 弾きbotの勝率0）。
//   比較用の切替としてだけ残す。経緯＝src/attacks.ts の exec_cannonMortar のコメント。
let currentMortarRows: ExecutionerMortarRowCount = 3
export function getExecutionerMortarRows(): ExecutionerMortarRowCount { return currentMortarRows }
export function setExecutionerMortarRows(rows: ExecutionerMortarRowCount): void {
  if (rows !== 3 && rows !== 5) return
  currentMortarRows = rows
  getAttack('exec_cannonMortar').hazardSpawns = EXECUTIONER_MORTAR_ROW_OFFSETS[rows].map((offset) => ({
    anchor: 'player', offset, width: 90, telegraphMs: 2000, activeMs: 360,
    damage: 20, postureDamage: 32, hitOnce: true, kind: 'quake',
  }))
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function easeOutQuint(progress: number): number {
  const p = clamp(progress, 0, 1)
  return 1 - (1 - p) ** 5
}

// 残り距離を「最大歩幅を超えない最少歩数」で等分する。歩数は歩ごとの開始時に持ち直す。
function forwardStepDistance(
  remainingPx: number,
  cfg: ExecutionerStepConfig,
  styleCfg: ExecutionerStepStyleConfig,
): number {
  if (remainingPx <= cfg.common.deadbandPx) return 0
  const stepCount = Math.max(1, Math.ceil(remainingPx / styleCfg.maxStepPx))
  const equalStepPx = remainingPx / stepCount
  // n>=2 の等分が将来の設定変更で40pxを割らないためのガード。n=1は到達点を越えない。
  return stepCount >= 2
    ? clamp(equalStepPx, cfg.common.minStepPx, styleCfg.maxStepPx)
    : Math.min(equalStepPx, styleCfg.maxStepPx)
}

// engine と visual が同じ移動相の尺を使う唯一の計算口。
export function executionerStepTravelMs(
  stepPx: number,
  cfg: ExecutionerStepConfig,
  styleCfg: ExecutionerStepStyleConfig,
): number {
  if (styleCfg.travelMode === 'fixed') {
    return Math.min(styleCfg.travelMs, cfg.common.maxTravelMsHardLimit)
  }
  return clamp(
    (stepPx / styleCfg.travelSpeedPxPerS) * 1000,
    styleCfg.minTravelMs,
    cfg.common.maxTravelMsHardLimit,
  )
}

function travelProgress(styleCfg: ExecutionerStepStyleConfig, elapsedMs: number, travelMs: number): number {
  const progress = travelMs > 0 ? elapsedMs / travelMs : 1
  return styleCfg.ease === 'linear' ? clamp(progress, 0, 1) : easeOutQuint(progress)
}

// 長い一歩の設定 (step.long の時だけ・咆哮の新版の真ん中へ移る一歩)。上限時間と歩幅の上限だけを延ばす。
export const EXECUTIONER_LONG_STEP_MAX_PX = 500
export function executionerLongStepCfg(cfg: ExecutionerStepConfig): ExecutionerStepConfig {
  return {
    ...cfg,
    common: { ...cfg.common, maxTravelMsHardLimit: EXECUTIONER_LONG_STEP_MAX_PX },
    styles: { ...cfg.styles, lunge: { ...cfg.styles.lunge, maxStepPx: EXECUTIONER_LONG_STEP_MAX_PX } },
  } as unknown as ExecutionerStepConfig
}
function cfgForStep(step: ExecutionerStepState, cfg: ExecutionerStepConfig): ExecutionerStepConfig {
  return step.long ? executionerLongStepCfg(cfg) : cfg
}

function travelXAt(
  step: ExecutionerStepState,
  baseCfg: ExecutionerStepConfig,
  styleCfg: ExecutionerStepStyleConfig,
  elapsedMs: number,
): number {
  const cfg = cfgForStep(step, baseCfg)
  // 後退は本体と同じ等速・後退用の時間で辿る (前ステップの緩急で引くと残像が体より先へ出る)。
  if (step.kind === 'back') {
    const backMs = executionerBackTravelMs(Math.abs(step.toX - step.fromX), cfg)
    return step.fromX + (step.toX - step.fromX) * backStepProgress(backMs > 0 ? elapsedMs / backMs : 1)
  }
  const travelMs = step.travelMs ?? executionerStepTravelMs(Math.abs(step.toX - step.fromX), cfg, styleCfg)
  const progress = travelProgress(styleCfg, elapsedMs, travelMs)
  return step.fromX + (step.toX - step.fromX) * progress
}

// 描画用の位置・可視性・残像を一か所で決める。render.ts はこの結果を合成するだけにする。
export function executionerStepVisual(
  step: ExecutionerStepState,
  baseCfg: ExecutionerStepConfig,
  nowStyleCfg: ExecutionerStepStyleConfig,
): ExecutionerStepVisual {
  const cfg = cfgForStep(step, baseCfg)
  // 後退 (砲形態の距離取り) にも前ステップと同じ残像を引く (2026-10-02 AI点検の候補8→ユーザー「残像があれば良さそう」)。
  //   旧は後退だけ残像なし＝0.2 秒で 300px 前後を立ち姿のまま滑り、壁際から反対側へ瞬間移動したように見えた。
  //   体は消さない・行き過ぎの戻しもしない (前ステップの瞬歩/着地の演出は後退には付けない)。
  const back = step.kind === 'back'
  const direction = step.toX < step.fromX ? -1 : 1
  const travelMs = back
    ? executionerBackTravelMs(Math.abs(step.toX - step.fromX), cfg)
    : step.travelMs ?? executionerStepTravelMs(Math.abs(step.toX - step.fromX), cfg, nowStyleCfg)
  const ageMs = step.phase === 'travel'
    ? step.elapsedMs
    : step.phase === 'land'
      ? travelMs + step.elapsedMs
      : Number.POSITIVE_INFINITY
  const life = cfg.common.afterimageLifeMs
  const fade = life > 0 ? clamp(1 - ageMs / life, 0, 1) : 0
  const ghosts: { x: number; alpha: number }[] = []

  if (fade > 0 && (step.phase === 'travel' || step.phase === 'land')) {
    if (step.style === 'shunpo' && !back) {
      const pathU = [0.2, 0.45, 0.7]
      for (let i = 0; i < Math.min(nowStyleCfg.afterimageCount, pathU.length); i += 1) {
        const alpha = nowStyleCfg.afterimageAlpha * fade * (1 - i / nowStyleCfg.afterimageCount)
        if (alpha <= 0) continue
        const u = pathU[i]!
        ghosts.push({ x: step.fromX + (step.toX - step.fromX) * u, alpha })
      }
    } else {
      const elapsedMs = step.phase === 'travel' ? step.elapsedMs : travelMs
      const lagsMs = [40, 80]
      for (let i = 0; i < Math.min(nowStyleCfg.afterimageCount, lagsMs.length); i += 1) {
        const alpha = nowStyleCfg.afterimageAlpha * fade * (1 - i / nowStyleCfg.afterimageCount)
        if (alpha <= 0) continue
        ghosts.push({
          x: travelXAt(step, cfg, nowStyleCfg, Math.max(0, elapsedMs - lagsMs[i]!)),
          alpha,
        })
      }
    }
  }

  let bodyOffsetX = 0
  if (!back && step.phase === 'land' && nowStyleCfg.overshootMs > 0) {
    const returnProgress = 1 - (1 - clamp(step.elapsedMs / nowStyleCfg.overshootMs, 0, 1)) ** 2
    bodyOffsetX = direction * nowStyleCfg.overshootPx * (1 - returnProgress)
  }
  return {
    bodyVisible: back || !(step.style === 'shunpo' && step.phase === 'travel'),
    bodyOffsetX,
    ghosts,
    bodyScaleY: stepWeightSquashY(step, step.crouchMs ?? cfg.common.crouchMs),
    ageMs: step.phase === 'crouch' ? -1 : ageMs,
    travelMs,
  }
}

// 処刑台型ステップの唯一の時間進行。ゲーム本体とラボが共用し、座標クランプだけを呼び出し側へ任せる。
// step=null は前進開始を意味する。後退は壁クランプ後の toX が必要なので、呼び出し側で state を組んで渡す。
// 後退の移動時間。通常の後退 (≤ back.px) は Tekken 型の 33ms 固定。
//   砲形態の距離維持 (2026-08-24・最大 230px) は同じ 33ms だと瞬間移動に見えるので、
//   back.px を超える分は前ステップと同じ 1000px/s で伸ばす (上限 = maxTravelMsHardLimit)。
function executionerBackTravelMs(distancePx: number, cfg: ExecutionerStepConfig): number {
  const fixed = Math.min(cfg.back.travelMs, cfg.common.maxTravelMsHardLimit)
  if (distancePx <= cfg.back.px) return fixed
  const bySpeed = distancePx / cfg.styles.lunge.travelSpeedPxPerS * 1000
  return Math.min(cfg.common.maxTravelMsHardLimit, Math.max(fixed, bySpeed))
}

// 壁際 (§3 案A・2026-08-29 ユーザー承認): 後退したい方向の壁が近い時のすれ違い先。
//   プレイヤーの位置から「壁と反対側」へ retreatToPx (=320・🔵2026-09-03第3便 ユーザー裁定) だけ
//   離れた座標を部屋の境界内へクランプし、クランプ後もプレイヤーから
//   tooClosePx (=240・🔵2026-09-03第3便 ユーザー裁定) 以上離れられる時だけ座標を返す。
//   届かない (クランプで近すぎになる) 時は null = 呼び出し側は現状どおり留まる。
//   出所 = docs/candidates/2026-08-28-executioner-cannon-wall-hug-and-spawn.md §3
export function executionerSidestepTargetX(
  playerX: number,
  awayFromPlayerDir: 1 | -1,
  bounds: { leftWall: number; rightWall: number },
  cfg: { retreatToPx: number; tooClosePx: number },
): number | null {
  const sideDir = awayFromPlayerDir > 0 ? -1 : 1
  const raw = playerX + sideDir * cfg.retreatToPx
  const targetX = clamp(raw, bounds.leftWall, bounds.rightWall)
  return Math.abs(targetX - playerX) >= cfg.tooClosePx ? targetX : null
}

export function advanceExecutionerStep(
  step: ExecutionerStepState | null,
  x: number,
  remainingPx: number,
  dtMs: number,
  baseCfg: ExecutionerStepConfig,
  effectiveWalkSpeed: number,
  style: ExecutionerStepStyle,
): ExecutionerStepAdvance {
  let cfg = baseCfg
  let nextStep = step
  let nextX = x
  let remainingDtMs = Math.max(0, dtMs)

  if (nextStep === null) {
    const remainingAbsPx = Math.abs(remainingPx)
    if (remainingAbsPx <= cfg.common.deadbandPx) return { step: null, x }
    const direction = remainingPx < 0 ? -1 : 1
    const styleCfg = cfg.styles[style]
    const stepPx = forwardStepDistance(remainingAbsPx, cfg, styleCfg)
    const travelMs = executionerStepTravelMs(stepPx, cfg, styleCfg)
    const settleMs = Math.max(
      cfg.common.minSettleMs,
      effectiveWalkSpeed > 0
        ? (stepPx / effectiveWalkSpeed) * 1000 - cfg.common.crouchMs - travelMs - cfg.common.landMs
        : cfg.common.minSettleMs,
    )
    nextStep = {
      kind: 'forward',
      style,
      phase: 'crouch',
      elapsedMs: 0,
      fromX: x,
      toX: x + direction * stepPx,
      settleMs,
      direction,
      commitOnLand: false,
    }
  }

  while (nextStep !== null && remainingDtMs > 1e-9) {
    cfg = cfgForStep(nextStep, baseCfg)
    const styleCfg = cfg.styles[nextStep.style]
    if (nextStep.phase === 'crouch') {
      nextX = nextStep.fromX
      const remainingAbsPx = Math.abs(remainingPx)
      const stepPx = forwardStepDistance(remainingAbsPx, cfg, styleCfg)
      const travelMs = executionerStepTravelMs(stepPx, cfg, styleCfg)
      const settleMs = Math.max(
        cfg.common.minSettleMs,
        effectiveWalkSpeed > 0
          ? (stepPx / effectiveWalkSpeed) * 1000 - cfg.common.crouchMs - travelMs - cfg.common.landMs
          : cfg.common.minSettleMs,
      )
      nextStep = {
        ...nextStep,
        toX: nextStep.fromX + nextStep.direction * stepPx,
        settleMs,
      }
      const phaseRemainingMs = Math.max(0, cfg.common.crouchMs - nextStep.elapsedMs)
      const consumedMs = Math.min(remainingDtMs, phaseRemainingMs)
      const elapsedMs = nextStep.elapsedMs + consumedMs
      remainingDtMs -= consumedMs
      if (elapsedMs + 1e-9 < cfg.common.crouchMs) {
        nextStep = { ...nextStep, elapsedMs }
        break
      }
      if (remainingAbsPx <= cfg.common.deadbandPx || Math.sign(remainingPx) !== nextStep.direction) {
        return { step: null, x: nextStep.fromX }
      }
      nextStep = { ...nextStep, phase: 'travel', elapsedMs: 0 }
      continue
    }

    if (nextStep.phase === 'travel') {
      const travelMs = nextStep.kind === 'forward'
        ? executionerStepTravelMs(Math.abs(nextStep.toX - nextStep.fromX), cfg, styleCfg)
        : executionerBackTravelMs(Math.abs(nextStep.toX - nextStep.fromX), cfg)
      const phaseRemainingMs = Math.max(0, travelMs - nextStep.elapsedMs)
      const consumedMs = Math.min(remainingDtMs, phaseRemainingMs)
      const elapsedMs = nextStep.elapsedMs + consumedMs
      const progress = nextStep.kind === 'back'
        ? backStepProgress(travelMs > 0 ? elapsedMs / travelMs : 1)
        : travelProgress(styleCfg, elapsedMs, travelMs)
      nextX = nextStep.fromX + (nextStep.toX - nextStep.fromX) * progress
      remainingDtMs -= consumedMs
      if (elapsedMs + 1e-9 < travelMs) {
        nextStep = { ...nextStep, elapsedMs }
        break
      }
      nextX = nextStep.toX
      nextStep = { ...nextStep, phase: 'land', elapsedMs: 0 }
      continue
    }

    if (nextStep.phase === 'land') {
      nextX = nextStep.toX
      const landMs = nextStep.kind === 'forward' ? cfg.common.landMs : cfg.back.freezeMs
      const phaseRemainingMs = Math.max(0, landMs - nextStep.elapsedMs)
      const consumedMs = Math.min(remainingDtMs, phaseRemainingMs)
      const elapsedMs = nextStep.elapsedMs + consumedMs
      remainingDtMs -= consumedMs
      if (elapsedMs + 1e-9 < landMs) {
        nextStep = { ...nextStep, elapsedMs }
        break
      }
      nextStep = nextStep.kind === 'back'
        ? null
        : { ...nextStep, phase: 'settle', elapsedMs: 0 }
      continue
    }

    nextX = nextStep.toX
    const phaseRemainingMs = Math.max(0, nextStep.settleMs - nextStep.elapsedMs)
    const consumedMs = Math.min(remainingDtMs, phaseRemainingMs)
    const elapsedMs = nextStep.elapsedMs + consumedMs
    remainingDtMs -= consumedMs
    nextStep = elapsedMs + 1e-9 < nextStep.settleMs
      ? { ...nextStep, elapsedMs }
      : null
  }

  return { step: nextStep, x: nextX }
}

export const EXECUTIONER_BOSS_ID = 'the-executioner'
export function isExecutioner(boss: { id: string }): boolean {
  return boss.id === EXECUTIONER_BOSS_ID
}

export type ExecutionerMuls = { gapMul: number; damageMul: number; postureMul: number }
const NEUTRAL_MULS: ExecutionerMuls = { gapMul: 1, damageMul: 1, postureMul: 1 }

export function mulsFor(exec: ExecutionerRuntime | undefined): ExecutionerMuls {
  if (!exec) return NEUTRAL_MULS
  return EXECUTIONER.weapons[exec.held]
}

export type ExecutionerRangeTuning = { preferredRange: number; attackCommitRange: number }

export function executionerMorphTiming(exec: Pick<ExecutionerRuntime, 'morphTurnRequired'>): {
  turnMs: number
  shoveHitAtMs: number
  shoveEndMs: number
  durationMs: number
} {
  const turnMs = exec.morphTurnRequired ? EXECUTIONER.transform.shoveTurnMs : 0
  return {
    turnMs,
    shoveHitAtMs: turnMs + EXECUTIONER.transform.shoveHitAtMs,
    shoveEndMs: turnMs + EXECUTIONER.transform.shoveMs,
    durationMs: turnMs + EXECUTIONER.transform.durationMs,
  }
}

export function rangeTuningFor(
  exec: ExecutionerRuntime,
  executesTaken: number,
): ExecutionerRangeTuning {
  // 幕1は外部のデバッグ操作で cannon を注入されても、刃の間合いを維持する。
  const weapon: ExecutionerWeapon = executesTaken === 0 ? 'blade' : exec.held
  // 🔵 2026-09-03 第4便: 砲は実行時に切り替え可能な cannonRangeTuning() を読む (EXECUTIONER.ranges.cannon は参照禁止)。
  if (weapon === 'cannon') {
    const tuning = cannonRangeTuning()
    return { preferredRange: tuning.preferredRange, attackCommitRange: tuning.attackCommitRange }
  }
  return EXECUTIONER.ranges.blade
}

// ===== 骨格 × 武器 の対応表 =====
// 刃の骨格を維持し、幕ゲートは 2026-10-02 裁定（地割りを幕1へ前倒し）に合わせる。
// 腕砲3本も同じ表へ載せ、BossDefinition.unlockByExecutes の鏡を手書きしない。
// 🔴 相乗りの絶対条件 = 同じ骨格の中で正解防御を揃える。
//   これは刃（近接）だけに適用する。砲は弾を見て読む別文法で、骨格法則の対象外。
//   出所 = docs/decision-ledger.md §請われぬ介錯 2026-08-12 裁定④。
export type ExecutionerFrame = 'drop' | 'ground' | 'reach'
export type ExecutionerAttackEntry = { id: string; atExecutes?: number }

export const EXECUTIONER_ATTACKS: Record<ExecutionerWeapon, Record<ExecutionerFrame, readonly ExecutionerAttackEntry[]>> = {
  blade: {
    // 技IDは maulDrop/maulSet/... のまま据え置き（2026-08-18 裁定・改名の対象外）。
    drop: [{ id: 'exec_maulDrop' }, { id: 'exec_maulSet' }, { id: 'exec_maulHold' }],
    ground: [{ id: 'exec_maulQuake' }],
    reach: [{ id: 'exec_maulJab' }],
  },
  cannon: {
    drop: [{ id: 'exec_cannonShot', atExecutes: 1 }],
    ground: [{ id: 'exec_cannonMortar', atExecutes: 1 }],
    reach: [{ id: 'exec_cannonVolley', atExecutes: 1 }],
  },
}

const EXEC_WEAPON_ORDER = ['blade', 'cannon'] as const
const EXEC_FRAME_ORDER = ['drop', 'ground', 'reach'] as const

// 🔵 2026-09-03 第4便: 砲形態で密着 (cannonRangeTuning().sweepWithinPx) されたときの強制選択技。
//   EXECUTIONER_ATTACKS の frame 表には入れない (抽選プールに乗せない・boss.ts が直接強制する)。
export const EXECUTIONER_PUNISH_ATTACK_IDS = ['exec_cannonSweep'] as const

export const EXECUTIONER_ATTACK_IDS: string[] =
  EXEC_WEAPON_ORDER.flatMap((weapon) =>
    EXEC_FRAME_ORDER.flatMap((frame) => EXECUTIONER_ATTACKS[weapon][frame].map((entry) => entry.id)))
    .concat(EXECUTIONER_PUNISH_ATTACK_IDS)

// 幕1から刃の3骨格を解禁。幕2の空段は、技単位で解禁する砲3本の鏡を導出するために残す。
export const EXECUTIONER_FRAME_UNLOCK: Array<{ atExecutes: number; frames: ExecutionerFrame[] }> = [
  { atExecutes: 0, frames: ['drop', 'reach', 'ground'] },
  { atExecutes: 1, frames: [] },
]

export function framesUnlocked(executesTaken: number): ExecutionerFrame[] {
  const out: ExecutionerFrame[] = []
  for (const stage of EXECUTIONER_FRAME_UNLOCK) {
    if (executesTaken >= stage.atExecutes) out.push(...stage.frames)
  }
  return out
}

export function executionerPool(exec: ExecutionerRuntime, executesTaken: number): string[] {
  // 幕1は常に刃。外部のデバッグ操作で cannon を入れても砲技を漏らさない。
  const weapon: ExecutionerWeapon = executesTaken === 0 ? 'blade' : exec.held
  return framesUnlocked(executesTaken).flatMap((frame) =>
    EXECUTIONER_ATTACKS[weapon][frame]
      .filter((entry) => executesTaken >= (entry.atExecutes ?? 0))
      .map((entry) => entry.id))
}

export const EXECUTIONER_UNLOCK_BY_EXECUTES: Array<{ atExecutes: number; ids: string[] }> =
  EXECUTIONER_FRAME_UNLOCK.map((stage) => {
    const newFrames = new Set<ExecutionerFrame>(stage.frames)
    const framesNow = framesUnlocked(stage.atExecutes)
    return {
      atExecutes: stage.atExecutes,
      ids: EXEC_WEAPON_ORDER.flatMap((weapon) => framesNow.flatMap((frame) =>
        EXECUTIONER_ATTACKS[weapon][frame]
          .filter((entry) => newFrames.has(frame)
            ? stage.atExecutes >= (entry.atExecutes ?? 0)
            : (entry.atExecutes ?? 0) === stage.atExecutes)
          .map((entry) => entry.id))),
    }
  })

// 攻撃抽選と同じく、attackCount から決定的に判定する。Math.random は使わない。
function hash01(n: number): number {
  const s = Math.sin(n * 91.7 + 12.9898) * 43758.5453
  return s - Math.floor(s)
}

export function shouldStartTransform(
  exec: ExecutionerRuntime,
  executesTaken: number,
  attackCount: number,
): boolean {
  if (executesTaken < 1 || exec.morph !== 'no') return false
  // 🔵 2026-09-05「時間の床」: 砲形態に変わった直後は、手数・確率の条件を満たしていても刃へ戻さない。
  //   刃形態 (blade→cannon への変形) には一切影響させない。
  if (exec.held === 'cannon' && exec.cannonHeldMs < getExecutionerCannonHoldMs()) return false
  if (exec.attacksSinceTransform < EXECUTIONER.transform.attacksBeforeCheck) return false
  return hash01(attackCount * 3.11 + exec.transformCount * 7.73 + 0.37) < EXECUTIONER.transform.chance
}

// recovery→idle の境界で1手を数え、条件を満たした時だけ変形アクションを予約する。
export function onAttackFinished(
  exec: ExecutionerRuntime,
  executesTaken: number,
  attackCount: number,
  // 🔵 2026-09-04: 終えた技の id。砲身薙ぎ (EXECUTIONER_PUNISH_ATTACK_IDS) は変形カウンタに数えず、冷却だけ入れる。
  //   未指定 = 従来どおり数える (旧テスト・他経路の互換)。
  finishedAttackId?: string,
): ExecutionerRuntime {
  if (executesTaken < 1) {
    return {
      ...exec,
      held: 'blade',
      attacksSinceTransform: 0,
      morph: 'no',
      morphElapsedMs: 0,
      morphTurnRequired: null,
      step: null,
    }
  }
  // 🔵 2026-09-03 第4便: 砲は攻撃を撃ち終わるたびに距離維持の門を開け直す（冷却は idle でしか減らず、2発目以降が至近撃ちになっていた）。
  const isPunish = finishedAttackId !== undefined
    && (EXECUTIONER_PUNISH_ATTACK_IDS as readonly string[]).includes(finishedAttackId)
  if (isPunish) {
    // 砲身薙ぎ＝密着への返しであって砲の「手番」ではない。数えない・変形判定もしない。
    //   冷却を入れ、距離維持の門 (backstepCooldownMs) を開けて、次の idle で後退へ落とす。
    return { ...exec, step: null, backstepCooldownMs: 0, sweepCooldownMs: EXECUTIONER_CANNON_SWEEP.cooldownMs }
  }
  const counted = {
    ...exec, step: null, attacksSinceTransform: exec.attacksSinceTransform + 1,
    ...(exec.held === 'cannon' ? { backstepCooldownMs: 0 } : {}),
  }
  return shouldStartTransform(counted, executesTaken, attackCount)
    ? { ...counted, morph: 'morphing', morphElapsedMs: 0, morphTurnRequired: null }
    : counted
}

// 変形中は boss.ts の idle 冒頭でここだけを進める。被弾/体幹側には特例を入れない。
export function stepMorph(exec: ExecutionerRuntime, dtMs: number): ExecutionerRuntime {
  if (exec.morph !== 'morphing') return exec
  const elapsed = exec.morphElapsedMs + dtMs
  if (elapsed < executionerMorphTiming(exec).durationMs) return { ...exec, morphElapsedMs: elapsed }
  return {
    ...exec,
    held: exec.held === 'blade' ? 'cannon' : 'blade',
    attacksSinceTransform: 0,
    transformCount: exec.transformCount + 1,
    morph: 'no',
    morphElapsedMs: 0,
    morphTurnRequired: null,
    sweepCooldownMs: 0,
  }
}

// ============================================================================
// 🔴 2026-08-13 夜 却下＝set_backstep の前傾姿勢が、後退時に滑って見える。
// 戻し方＝この定数を EXECUTIONER.transform へ戻し、boss.ts の同名退避分岐を
// idle の morphing 節へ復元し、sprites.ts の前半を set_backstep へ戻す。
// 実行経路からは参照せず、却下前の値だけを保存する。
// ============================================================================
export const ARCHIVED_EXECUTIONER_MORPH_BACKOFF_2026_08_13 = {
  backOffMs: 500,
  backOffSpeedPx: 260,
} as const

// ============================================================================
// 🔴 2026-08-12 裁定で退避（枷・車輪・柄・破壊・ゴミ山拾い直しの全滅）。
// 戻し方 = 下の設定と関数を現行型へ戻し、hits/boss/combat/game/sprites の旧接点を再配線する。
// 実行経路からは参照しない。既存資産を消さず、旧仕様の数値と決定的抽選だけを保存する。
// ============================================================================
export const ARCHIVED_EXECUTIONER_BREAK_FETCH_2026_08_12 = {
  weapons: {
    maul: { parriesToBreak: 4, gapMul: 1.15, damageMul: 1.15, postureMul: 1.15 },
    yoke: { parriesToBreak: 5, gapMul: 1.0, damageMul: 0.9, postureMul: 1.0 },
    wheel: { parriesToBreak: 3, gapMul: 0.85, damageMul: 1.0, postureMul: 1.25 },
  },
  haft: { attacksBeforeFetch: 3, gapMul: 0.55, damageMul: 0.45, postureMul: 0.6 },
  fetch: {
    walkSpeedMul: 1.25,
    digMs: 2500,
    postureGainMul: 0,
    hpDamageMul: 1.6,
    pileXs: [150, 810] as readonly number[],
    arriveTolerancePx: 24,
  },
} as const

export type ArchivedExecutionerWeapon = 'maul' | 'yoke' | 'wheel'
export type ArchivedExecutionerHeld = ArchivedExecutionerWeapon | 'haft'
export type ArchivedExecutionerFetch = 'no' | 'walk' | 'dig'
export type ArchivedExecutionerRuntime = {
  held: ArchivedExecutionerHeld
  brokenFrom: ArchivedExecutionerWeapon | null
  parriesTaken: number
  haftAttacksLeft: number
  fetch: ArchivedExecutionerFetch
  fetchElapsedMs: number
  pileX: number
  pickCount: number
}

export function archivedCreateExecutionerRuntime(): ArchivedExecutionerRuntime {
  return {
    held: 'maul', brokenFrom: null, parriesTaken: 0, haftAttacksLeft: 0,
    fetch: 'no', fetchElapsedMs: 0, pileX: 0, pickCount: 0,
  }
}

export function archivedIsDefenselessFetch(boss: { exec?: ArchivedExecutionerRuntime }): boolean {
  return boss.exec?.fetch === 'dig'
}

export function archivedMulsFor(exec: ArchivedExecutionerRuntime | undefined): ExecutionerMuls {
  if (!exec) return NEUTRAL_MULS
  if (exec.held === 'haft') {
    const haft = ARCHIVED_EXECUTIONER_BREAK_FETCH_2026_08_12.haft
    return { gapMul: haft.gapMul, damageMul: haft.damageMul, postureMul: haft.postureMul }
  }
  const weapon = ARCHIVED_EXECUTIONER_BREAK_FETCH_2026_08_12.weapons[exec.held]
  return { gapMul: weapon.gapMul, damageMul: weapon.damageMul, postureMul: weapon.postureMul }
}

export function archivedPickNextWeapon(
  exclude: ArchivedExecutionerWeapon | null,
  pickCount: number,
): ArchivedExecutionerWeapon {
  const all: ArchivedExecutionerWeapon[] = ['maul', 'yoke', 'wheel']
  const pool = exclude ? all.filter((weapon) => weapon !== exclude) : all
  const index = Math.floor(hash01(pickCount * 3.11 + 0.37) * pool.length)
  return pool[Math.min(pool.length - 1, index)]!
}

export function archivedOnParried(
  exec: ArchivedExecutionerRuntime,
): { exec: ArchivedExecutionerRuntime; broke: boolean } {
  if (exec.held === 'haft') return { exec, broke: false }
  const parriesTaken = exec.parriesTaken + 1
  const limit = ARCHIVED_EXECUTIONER_BREAK_FETCH_2026_08_12.weapons[exec.held].parriesToBreak
  if (parriesTaken < limit) return { exec: { ...exec, parriesTaken }, broke: false }
  return {
    exec: {
      ...exec,
      held: 'haft',
      brokenFrom: exec.held,
      parriesTaken: 0,
      haftAttacksLeft: ARCHIVED_EXECUTIONER_BREAK_FETCH_2026_08_12.haft.attacksBeforeFetch,
    },
    broke: true,
  }
}

export function archivedNearestPileX(bossX: number): number {
  const pileXs = ARCHIVED_EXECUTIONER_BREAK_FETCH_2026_08_12.fetch.pileXs
  let best = pileXs[0]
  for (const x of pileXs) {
    if (Math.abs(x - bossX) < Math.abs(best - bossX)) best = x
  }
  return best
}

export function archivedOnAttackFinished(
  exec: ArchivedExecutionerRuntime,
  bossX: number,
): ArchivedExecutionerRuntime {
  if (exec.held !== 'haft' || exec.fetch !== 'no') return exec
  const left = exec.haftAttacksLeft - 1
  if (left > 0) return { ...exec, haftAttacksLeft: left }
  return {
    ...exec,
    haftAttacksLeft: 0,
    fetch: 'walk',
    fetchElapsedMs: 0,
    pileX: archivedNearestPileX(bossX),
  }
}

export function archivedStepFetch(
  exec: ArchivedExecutionerRuntime,
  bossX: number,
  walkSpeedPxPerSec: number,
  dtSec: number,
): { exec: ArchivedExecutionerRuntime; x: number; picked: ArchivedExecutionerWeapon | null } {
  if (exec.fetch === 'no') return { exec, x: bossX, picked: null }
  const fetch = ARCHIVED_EXECUTIONER_BREAK_FETCH_2026_08_12.fetch
  if (exec.fetch === 'walk') {
    const dx = exec.pileX - bossX
    if (Math.abs(dx) <= fetch.arriveTolerancePx) {
      return { exec: { ...exec, fetch: 'dig', fetchElapsedMs: 0 }, x: bossX, picked: null }
    }
    const step = Math.sign(dx) * walkSpeedPxPerSec * fetch.walkSpeedMul * dtSec
    const x = Math.abs(step) >= Math.abs(dx) ? exec.pileX : bossX + step
    return { exec, x, picked: null }
  }
  const elapsedMs = exec.fetchElapsedMs + dtSec * 1000
  if (elapsedMs < fetch.digMs) {
    return { exec: { ...exec, fetchElapsedMs: elapsedMs }, x: bossX, picked: null }
  }
  const picked = archivedPickNextWeapon(exec.brokenFrom, exec.pickCount)
  return {
    exec: {
      ...exec,
      held: picked,
      brokenFrom: null,
      parriesTaken: 0,
      haftAttacksLeft: 0,
      fetch: 'no',
      fetchElapsedMs: 0,
      pickCount: exec.pickCount + 1,
    },
    x: bossX,
    picked,
  }
}
